import { useRef, useState } from "react";
import type { Chart } from "@catchup/shared";
import { useStore } from "../store.ts";
import {
  checkKey,
  setStoredKey,
  storedKey,
  streamChart,
  uploadTrack,
} from "../api.ts";

type Phase = "idle" | "uploading" | "charting" | "done";

/**
 * Add a track: upload, then chart it server-side.
 *
 * Gated behind a passphrase. The server holds the CDN's write token and the
 * CDN serves reads publicly and permanently, so an ungated upload route would
 * turn this into anonymous music hosting — see auth.ts on the server.
 */
export function Add() {
  const store = useStore();
  const [key, setKey] = useState(storedKey());
  const [keyError, setKeyError] = useState<string | null>(null);
  const [checking, setChecking] = useState(false);

  const [file, setFile] = useState<File | null>(null);
  const [artist, setArtist] = useState("");
  const [title, setTitle] = useState("");
  const [album, setAlbum] = useState("");

  const [phase, setPhase] = useState<Phase>("idle");
  const [uploadPct, setUploadPct] = useState(0);
  const [progress, setProgress] = useState<{ message: string; pct: number } | null>(null);
  const [warnings, setWarnings] = useState<string[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [chart, setChart] = useState<Chart | null>(null);
  const abort = useRef<AbortController | null>(null);

  async function verify() {
    setChecking(true);
    setKeyError(null);
    const res = await checkKey(key);
    setChecking(false);
    if (res.ok) {
      setStoredKey(key);
      useStore.getState().set({ authed: true });
    } else {
      setKeyError(res.error || "Incorrect passphrase.");
    }
  }

  async function go() {
    if (!file) return;
    setError(null);
    setWarnings([]);
    setChart(null);
    setPhase("uploading");
    setUploadPct(0);

    try {
      const up = await uploadTrack(file, setUploadPct);

      setPhase("charting");
      setProgress({ message: "Queued", pct: 0 });
      abort.current = new AbortController();

      for await (const ev of streamChart(
        {
          fileHash: up.fileHash,
          artist: artist || undefined,
          title: title || undefined,
          album: album || undefined,
        },
        abort.current.signal,
      )) {
        if (ev.type === "progress") {
          setProgress({ message: ev.message ?? "", pct: ev.pct ?? 0 });
        } else if (ev.type === "warning") {
          setWarnings((w) => [...w, ev.message ?? ""]);
        } else if (ev.type === "error") {
          setError(ev.message ?? "Charting failed.");
          setPhase("idle");
        } else if (ev.type === "result") {
          setChart(ev.chart as Chart);
          setPhase("done");
        }
      }
      if (phase !== "done") setProgress(null);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
      setPhase("idle");
    } finally {
      abort.current = null;
    }
  }

  const gated = store.uploadsGated && !store.authed;
  const maxMb = (store.maxUploadBytes / 1e6).toFixed(0);

  return (
    <>
      <div className="row" style={{ marginBottom: 4 }}>
        <button onClick={() => useStore.getState().backToLibrary()}>← Library</button>
      </div>

      {!store.uploadsConfigured && (
        <p className="note bad">
          This deployment has no contribution passphrase configured, so adding
          tracks is disabled. Set <code className="inline">CATCHUP_UPLOAD_KEY</code>{" "}
          on the server.
        </p>
      )}

      {gated && store.uploadsConfigured && (
        <div className="panel">
          <h2>Contribution passphrase</h2>
          <p className="sub">
            Playing is open to everyone; adding tracks isn't. This server holds the
            CDN's write token and the CDN serves every stored file at a permanent
            public URL — so an open upload form here would be anonymous music
            hosting, not a game.
          </p>
          <label className="field">
            <span className="lbl">Passphrase</span>
            <input
              type="password"
              value={key}
              onChange={(e) => setKey(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && void verify()}
              placeholder="••••••••"
            />
          </label>
          <button className="primary" disabled={!key || checking} onClick={() => void verify()}>
            {checking ? "Checking…" : "Unlock"}
          </button>
          {keyError && <p className="note bad">{keyError}</p>}
        </div>
      )}

      {!gated && (
        <>
          <div className="panel">
            <h2>1 · Choose an audio file</h2>
            <p className="sub">
              Up to {maxMb}MB. It's uploaded, stored, and charted server-side — the
              chart takes a few minutes the first time and is kept forever after.
              Uploading a file that's already stored is instant.
            </p>
            <input
              type="file"
              accept="audio/*"
              disabled={phase === "uploading" || phase === "charting"}
              onChange={(e) => {
                const f = e.target.files?.[0] ?? null;
                setFile(f);
                setChart(null);
                setError(null);
                // Prefill from "Artist - Title.mp3", the common case.
                if (f) {
                  const stem = f.name.replace(/\.[^.]+$/, "");
                  const [a, t] = stem.split(" - ");
                  if (t && !artist && !title) {
                    setArtist(a!.trim());
                    setTitle(t.trim());
                  }
                }
              }}
            />
            {file && (
              <p className="note good">
                {file.name} — {(file.size / 1e6).toFixed(1)}MB
              </p>
            )}
          </div>

          <div className="panel">
            <h2>2 · Metadata (optional)</h2>
            <p className="sub">
              Leave blank to read the file's own tags, then fall back to
              fingerprinting. Filling these in makes the lyrics lookup much more
              reliable.
            </p>
            <div className="row">
              <label className="field" style={{ flex: 1, minWidth: 150 }}>
                <span className="lbl">Artist</span>
                <input type="text" value={artist} onChange={(e) => setArtist(e.target.value)} />
              </label>
              <label className="field" style={{ flex: 1, minWidth: 150 }}>
                <span className="lbl">Title</span>
                <input type="text" value={title} onChange={(e) => setTitle(e.target.value)} />
              </label>
              <label className="field" style={{ flex: 1, minWidth: 150 }}>
                <span className="lbl">Album</span>
                <input type="text" value={album} onChange={(e) => setAlbum(e.target.value)} />
              </label>
            </div>

            <div className="row">
              <button
                className="primary"
                disabled={!file || phase === "uploading" || phase === "charting"}
                onClick={() => void go()}
              >
                {phase === "uploading"
                  ? `Uploading ${(uploadPct * 100).toFixed(0)}%`
                  : phase === "charting"
                    ? "Charting…"
                    : "Upload and chart"}
              </button>
              {phase === "charting" && (
                <button onClick={() => abort.current?.abort()}>Cancel</button>
              )}
            </div>

            {phase === "uploading" && (
              <div className="bar">
                <div style={{ width: `${uploadPct * 100}%` }} />
              </div>
            )}

            {phase === "charting" && progress && (
              <>
                <div className="bar">
                  <div style={{ width: `${(progress.pct ?? 0) * 100}%` }} />
                </div>
                <p className="sub" style={{ margin: 0 }}>{progress.message}</p>
                <p className="sub">
                  Vocal isolation and forced alignment are the slow part — several
                  minutes on this hardware. You can leave this tab open; the job
                  keeps running if you come back.
                </p>
              </>
            )}

            {warnings.map((w, i) => (
              <p className="note warn" key={i}>{w}</p>
            ))}
            {error && <p className="note bad">{error}</p>}

            {chart && (
              <>
                <p className="note good">
                  <strong>{chart.meta.artist} — {chart.meta.title}</strong>
                  <br />
                  {chart.words.length} words,{" "}
                  {chart.sections.filter((s) => s.playable).length} playable sections,
                  built in {chart.buildSeconds}s ({chart.source}).
                </p>
                {chart.flags.map((f) => (
                  <p className={`note ${f.code === "lrc-only" ? "warn" : "bad"}`} key={f.code}>
                    <strong>{f.code}</strong> — {f.detail}
                  </p>
                ))}
                <button
                  className="primary"
                  onClick={() => useStore.getState().backToLibrary()}
                >
                  Back to library
                </button>
              </>
            )}
          </div>
        </>
      )}
    </>
  );
}
